/* Core hook managing guided walkthrough state: step navigation, element finding, click detection.
   Reuses the RAF-based element polling pattern from useOnboardingChat. */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getWalkthroughById,
  type GuidedWalkthrough,
  type WalkthroughStep,
} from "@/data/guidedWalkthroughs";

export interface UseGuidedWalkthroughReturn {
  isActive: boolean;
  start: (walkthroughId: string) => void;
  cancel: () => void;
  currentStep: WalkthroughStep | null;
  stepIndex: number;
  totalSteps: number;
  walkthroughName: string;
  targetRect: DOMRect | null;
  advance: () => void;
}

export function useGuidedWalkthrough(): UseGuidedWalkthroughReturn {
  const navigate = useNavigate();
  const location = useLocation();

  const [walkthrough, setWalkthrough] = useState<GuidedWalkthrough | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  const rafRef = useRef(0);
  const clickListenerRef = useRef<(() => void) | null>(null);
  const targetElRef = useRef<Element | null>(null);
  const advanceRef = useRef<() => void>(() => {});

  const isActive = walkthrough !== null;
  const currentStep = walkthrough?.steps[stepIndex] ?? null;
  const totalSteps = walkthrough?.steps.length ?? 0;
  const walkthroughName = walkthrough?.name ?? "";

  const cleanupClickListener = useCallback(() => {
    if (clickListenerRef.current && targetElRef.current) {
      targetElRef.current.removeEventListener("click", clickListenerRef.current, true);
      clickListenerRef.current = null;
      targetElRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    cleanupClickListener();
    cancelAnimationFrame(rafRef.current);
    setWalkthrough(null);
    setStepIndex(0);
    setTargetRect(null);
    document.body.style.overflow = "";
  }, [cleanupClickListener]);

  const advance = useCallback(() => {
    cleanupClickListener();
    setTargetRect(null);
    const nextIndex = stepIndex + 1;
    if (!walkthrough || nextIndex >= walkthrough.steps.length) {
      cancel();
      return;
    }
    setStepIndex(nextIndex);
  }, [stepIndex, walkthrough, cancel, cleanupClickListener]);

  // Keep ref in sync so click handlers always call the latest advance
  advanceRef.current = advance;

  const start = useCallback(
    (walkthroughId: string) => {
      const wt = getWalkthroughById(walkthroughId);
      if (!wt || wt.steps.length === 0) return;
      // Cancel any active walkthrough first
      cleanupClickListener();
      cancelAnimationFrame(rafRef.current);
      setTargetRect(null);
      setWalkthrough(wt);
      setStepIndex(0);
    },
    [cleanupClickListener],
  );

  // Navigate to the correct route and find the target element
  useEffect(() => {
    if (!isActive || !currentStep) {
      setTargetRect(null);
      return;
    }

    const step = currentStep;

    // Navigate if needed
    if (step.route && location.pathname !== step.route) {
      navigate(step.route, { replace: true });
    }

    let cancelled = false;
    const selector = step.targetSelector;
    const delay = step.delayMs ?? 300;

    function findElement() {
      if (cancelled) return;
      const el = document.querySelector(selector);
      if (el) {
        setTargetRect(el.getBoundingClientRect());
        targetElRef.current = el;

        // Attach click listener for auto-advance (use ref to avoid stale closure)
        if (step.autoAdvanceOnClick !== false) {
          const handler = () => {
            setTimeout(() => advanceRef.current(), 150);
          };
          el.addEventListener("click", handler, true);
          clickListenerRef.current = handler;
        }

        // Keep updating the rect on scroll/resize
        const updateRect = () => {
          if (cancelled) return;
          const freshEl = document.querySelector(selector);
          if (freshEl) {
            setTargetRect(freshEl.getBoundingClientRect());
          }
          rafRef.current = requestAnimationFrame(updateRect);
        };
        rafRef.current = requestAnimationFrame(updateRect);
        return;
      }
      rafRef.current = requestAnimationFrame(findElement);
    }

    const timeout = setTimeout(findElement, delay);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      cancelAnimationFrame(rafRef.current);
    };
    // advance is stable via useCallback but we need it for the click handler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, currentStep, stepIndex, location.pathname, navigate]);

  // Lock scroll while walkthrough is active and we have a target
  useEffect(() => {
    if (!isActive || !targetRect) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isActive, targetRect]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupClickListener();
      cancelAnimationFrame(rafRef.current);
      document.body.style.overflow = "";
    };
  }, [cleanupClickListener]);

  return {
    isActive,
    start,
    cancel,
    currentStep,
    stepIndex,
    totalSteps,
    walkthroughName,
    targetRect,
    advance,
  };
}
